/* eslint-disable @typescript-eslint/naming-convention */
import { put, call, takeLatest, select, all } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import {
  initialize,
  arrayPush,
  arrayRemoveAll,
  reset,
  change,
  getFormValues,
} from 'redux-form';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { browserHistory } from 'react-router';

import * as act from '../../actions';
import { showLoadingSpinner } from '../../actions';
import {
  INVENTORY_TRANSFER_PAGE_REQUEST,
  INVENTORY_TRANSFER_TRANSFERS_REQUEST,
  INVENTORY_TRANSFER_ON_CLICK_ROW,
  INVENTORY_TRANSFER_ON_CHANGE_OUTLET_IDS,
  INVENTORY_TRANSFER_ON_CHANGE_DATE,
  INVENTORY_TRANSFER_ON_CLICK_CREATE_TRANSFER,
  INVENTORY_TRANSFER_GET_ITEMS,
  INVENTORY_TRANSFER_ON_MODAL_SELECT_CHANGE,
  INVENTORY_TRANSFER_ON_MODAL_ADD_ITEM,
  INVENTORY_TRANSFER_RESET_TRANSFERS_FORM,
  INVENTORY_TRANSFER_ON_SUBMIT_CREATE_TRANSFER_FORM,
  INVENTORY_TRANSFER_ON_CONFIRM_CREATE_TRANSFER,
  INVENTORY_TRANSFER_ON_CONFIRM_ADVANCED_CREATE_TRANSFER,
  INVENTORY_TRANSFER_EXPORT,
  INVENTORY_TRANSFER_ON_CLICK_ADD_ITEM,
  INVENTORY_TRANSFER_ON_NAMESPACE_CHANGE,
  ADVANCE_INVENTORY_TRANSFER_SUBMIT_APPROVAL_FORM,
} from '../../actions/inventory/transfer/constants';
import * as actInvTrf from '../../actions/inventory/transfer';
import * as actInvAdvTrf from '../../actions/inventory/transfer/advancedTransfer';
import * as actNotif from '../../actions/notifications';
import * as actLoadingSpinner from '../../actions/loadingSpinner';
import { subscriptionStatus } from '../../actions/inventory/ingredientSubscription';
import * as actAdvancedInventorySettings from '../../actions/inventory/advancedInventorySettings';
import {
  NAMESPACE_ITEMS,
  NAMESPACE_INGREDIENTS,
  FILTER_DELETED_OUTLETS,
} from '../../actions/inventory/constants';

import * as apiOutlets from '../../api/outlets';
import * as apiProfile from '../../api/profile';
import * as apiTrf from '../../api/inventory/transfer';
import * as apiInvSettings from '../../api/inventory/advancedInventorySettings';
import * as apiIngTrf from '../../api/ingredientInventory/transfer';
import * as apiAdvItemTrf from '../../api/inventory/advancedTransfer';
import * as apiAdvIngTrf from '../../api/ingredientInventory/advancedTransfer';
import * as apiItem from '../../api/items';
import * as apiCurrentInventory from '../../api/currentInventoryDropdown';
import * as apiGetFilteredDate from '../../api/get-filtered-date';
import * as apiUpdateFilteredDate from '../../api/update-filtered-date';
import * as apiIngredients from '../../api/ingredients';
import { activityTransformer } from '../../api/inventory/transfer/transformer';
import * as apiAdvTrf from '../../api/inventory/transfer/advancedTransfer';
import { getAllOutlets } from '../../api/outlets/getAllOutlets/getAllOutlets';
import { setCurrentInventoryDropdownFromLocalStorage } from '../../utils/currentInventoryDropdown';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import { shouldShowDeleteButton } from '../../utils/transferDetail';
import * as clevertapInventoryTransfer from '../../clevertap/inventory/transfer';
import * as clevertapAdvancedTransfer from '../../clevertap/inventory/advancedTransfer';
import { TabTypeEnum } from '../../types/actionBars';
import { polling } from './utils/pollingSaga';
import {
  PARTIALLY_SHIPPED_THE_TRANSFER_REQUEST,
  FULLY_SHIPPED_THE_TRANSFER_REQUEST,
  ERROR_MESSAGE_ITEMS_OR_INGREDIENTS_MORE_THAN_50,
} from './constants';

import type {
  ItemsTransferRequestBody,
  IngredientsTransferRequestBody,
} from './types/transfer/createAdvancedTransfer.type';
import type {
  BaseTransfer,
  Item,
  Transfer,
} from '../../types/transfer/Transfer';

const ITEM_DOES_NOT_FOUND_MESSAGE = 'Item does not found or already removed.';
const INGREDIENT_DOES_NOT_FOUND_MESSAGE =
  'Ingredient does not found or already removed.';

const getUser = (state) => state.moka.user;
const getSelectedOutlets = (state) => state.inventory.transfer.selectedOutlets;
const getAllOutlet = (state) => state.inventory.transfer.outlets;
const getAdvancedTransferSetting = (state) =>
  state.inventory.advancedInventorySettings.permissions.advanced_transfer;
const getTabData = (state) => state.inventory.advancedTransfer.tabData;
const getTransfersType = (state) =>
  state.inventory.advancedTransfer.transfersType;
const getAssignedOutlets = (state) => state.outlet;

function* getAdvancedTransferTabPermissions() {
  /**
   * This saga worker will genereate and set
   * staff tab permissions for advanced transfer:
   * Approval, Shipment, Fulfillment
   */
  const APPROVE_TRANSFER = 'Approve Transfer';
  const TRANSFER_SHIPMENT = 'Transfer Shipment';
  const TRANSFER_FULFILLMENT = 'Transfer Fulfillment';
  /**
   * Request Transfer permission
   * is related for showing Create Transfer button
   * in All Transfers tab
   */
  const TRANSFER_REQUEST = 'Request Transfer';

  try {
    const {
      data: { permissions },
    } = yield call(apiProfile.getProfile, {});

    const filteredPermissions = permissions.filter((val) => {
      return [
        APPROVE_TRANSFER,
        TRANSFER_FULFILLMENT,
        TRANSFER_SHIPMENT,
        TRANSFER_REQUEST,
      ].includes(val.sub_menu_name);
    });

    const mappedPermissions = filteredPermissions.map((val) => {
      /**
       * Transform the filtered permissions to match transfersType values:
       * e.g from 'Transfer Shipment' to 'SHIPMENT'.
       * See transfersType Redux state to get more context.
       */
      const regex =
        /(approval|approve|shipment|fulfillment|request transfer)/gi;
      return val.sub_menu_name.match(regex)[0].toUpperCase();
    });

    yield put(actInvAdvTrf.setTabPermission(mappedPermissions));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

function replaceAdvancedTransferBrowserHash() {
  const { hash } = window.location;
  if (hash === '#advanced-transfer') {
    return;
  }

  browserHistory.push('/inventory/transfer#advanced-transfer');
}

function replaceSimpleTransferBrowserHash() {
  const { hash } = window.location;
  if (hash === '#simple-transfer') {
    return;
  }

  browserHistory.push('/inventory/transfer#simple-transfer');
}

function* getCogs(id: number, namespace: string, fromOutletId: number) {
  const isItem = namespace === NAMESPACE_ITEMS;
  const api = isItem ? apiAdvTrf.getItemCogs : apiAdvTrf.getIngredientCogs;

  try {
    const { data } = yield call(api, id, fromOutletId);
    return data;
  } catch (e) {
    assertErrorHasMessage(e);

    const inventoryItemRelatedError = e.message === ITEM_DOES_NOT_FOUND_MESSAGE;
    const inventoryIngredientRelatedError =
      e.message === INGREDIENT_DOES_NOT_FOUND_MESSAGE;

    if (inventoryItemRelatedError || inventoryIngredientRelatedError) {
      throw new Error(e.message);
    } else {
      /**
       * Both of these error handling below
       * only fires when it's not related to inventory item/ingredient error
       * e.g: failed to get item from inventory API due to the item is already been deleted
       */
      yield put(actInvAdvTrf.errorGetItem(true));
      yield put(actNotif.error(e.message));
      throw new Error(e.message);
    }
  }
}

function* eligibleToEditTransfer(transfer: Transfer) {
  const { outlets: assignedOutlets } = yield select(getAssignedOutlets);
  const { permissions } = yield select(getUser);
  const searchForm = yield select(getFormValues('search'));
  const transfersType = yield select(getTransfersType);

  const { outletIds: selectedOutletIds } = searchForm;
  const assignedOutletIds = assignedOutlets.map(
    (assignedOutlet) => assignedOutlet.id
  );
  const inventoryPermissions = permissions.filter(
    (permission) => permission.menu === 'No Menu'
  );
  const hasCreateRequestPermission =
    inventoryPermissions.findIndex(
      (permission) => permission.sub_menu === 'Create Transfer Request'
    ) !== -1;

  return shouldShowDeleteButton({
    transfer,
    transfersType,
    hasCreateRequestPermission,
    assignedOutletIds,
    selectedOutletIds,
  });
}

function* getAllItemCogs(
  transferItems: Item[],
  fromOutletId: number,
  transfer: BaseTransfer
) {
  try {
    const transferType = yield select(getTransfersType);
    const tabWithCogs = [TabTypeEnum.SHIPMENT, TabTypeEnum.APPROVAL].includes(
      transferType
    );
    const isEligibleToEditTransfer = yield call(
      eligibleToEditTransfer,
      transfer as Transfer
    );
    const shouldCallDetailItem = isEligibleToEditTransfer || tabWithCogs;

    if (!shouldCallDetailItem) {
      return transferItems.map((transferItem) => {
        const { item_name, item_variant_name } = transferItem;
        const isItemUnique = uniqBy(transferItems, 'item_id').length > 1;
        const isNameDifferent = item_name !== item_variant_name;
        const isMultiVariant = isItemUnique || isNameDifferent;

        return {
          ...transferItem,
          is_multi_variant: isMultiVariant,
        };
      });
    }

    const itemIds = uniq(transferItems.map((item) => item.item_id));
    const itemDetails = yield all(
      itemIds.map((itemId) => call(getCogs, itemId, 'items', fromOutletId))
    );

    const transferItemWithCogs = transferItems.map((transferItem) => {
      const { item_variant_id: variantId, item_id: itemId } = transferItem;

      const itemDetail = itemDetails.find(({ id }) => id === itemId);
      const itemVariants = itemDetail?.item_variants || [];
      const variant = itemVariants.find(({ id }) => id === variantId);
      const filteredVariants = itemVariants.filter((item) => item.track_stock);

      return {
        ...transferItem,
        cogs: variant?.cogs || 0,
        in_stock: variant?.in_stock,
        item_variants: filteredVariants,
        // This SKU is matched by item_variant_id to get the sku
        item_variant_sku: variant?.sku,
        is_multi_variant: itemVariants.length > 1,
      };
    });

    return transferItemWithCogs;
  } catch (error) {
    assertErrorHasMessage(error);
    throw new Error(error.message);
  }
}

function* getAllIngredientCogs(ingredients: any[], fromOutletId: number) {
  try {
    const transferIngredientWithCogs: any[] = [];

    const ingredientDetails = yield all(
      ingredients.map((ingredient) =>
        call(getCogs, ingredient.ingredient_id, 'ingredients', fromOutletId)
      )
    );

    ingredientDetails.forEach((ingredientDetail, index) => {
      const ingredient = ingredients[index];

      transferIngredientWithCogs.push({
        ...ingredient,
        in_stock: ingredientDetail?.in_stock,
        cogs: ingredientDetail?.cogs,
        type: ingredientDetail?.type,
      });
    });

    return transferIngredientWithCogs;
  } catch (error) {
    assertErrorHasMessage(error);
    throw new Error(error.message);
  }
}

function* itemTransferDetailFlow(transfer) {
  try {
    const { data: advancedInventoryItemDetail } = yield call(
      apiAdvItemTrf.getDetailItemTransfer,
      transfer.id
    );

    const partiallyShippedItems = advancedInventoryItemDetail.activities.filter(
      (activity) => activity.status === PARTIALLY_SHIPPED_THE_TRANSFER_REQUEST
    );
    const transferStatusIsPartial = !isEmpty(partiallyShippedItems);

    const { items } = advancedInventoryItemDetail;

    yield put(
      actInvTrf.setTransferWithActivities(
        activityTransformer(
          advancedInventoryItemDetail.activities,
          items,
          NAMESPACE_ITEMS
        )
      )
    );

    yield put(actInvAdvTrf.setDetailItemTransfer(advancedInventoryItemDetail));

    const transferItemWithCogs = yield call(
      getAllItemCogs,
      advancedInventoryItemDetail.items,
      advancedInventoryItemDetail.from_outlet_id,
      transfer
    );

    if (transferStatusIsPartial) {
      const finalShippedItems = advancedInventoryItemDetail.activities.filter(
        (activity) => activity.status === FULLY_SHIPPED_THE_TRANSFER_REQUEST
      );

      if (isEmpty(finalShippedItems)) {
        yield put(actInvAdvTrf.setPartialItemsTransfer(partiallyShippedItems));
      } else {
        const finalPartiallyShippedItems = [
          ...partiallyShippedItems,
          ...finalShippedItems,
        ];

        yield put(
          actInvAdvTrf.setPartialItemsTransfer(finalPartiallyShippedItems)
        );
      }
    }

    yield put(
      actInvTrf.setTransferDetail({
        ...transfer,
        items: transferItemWithCogs,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);

    /**
     * This error handling focus on populating required data
     * for all transferType FormDetail rendering.
     * This error occurs because one or one of the items in the transfer is/are deleted
     * in the middle of advanced transfer process.
     */

    const relatedToInventoryError =
      error.message === ITEM_DOES_NOT_FOUND_MESSAGE;

    if (relatedToInventoryError) {
      const tabData = yield select(getTabData);
      const transfersType = yield select(getTransfersType);

      const deletedItemTransferData = tabData[transfersType].data.find(
        (data) => data.id === transfer.id
      )?.items;

      yield put(
        actInvTrf.setTransferDetail({
          ...transfer,
          items: deletedItemTransferData,
        })
      );
    }

    if (!relatedToInventoryError) {
      yield put(actNotif.error(error.message));
    }
  }
}

function* ingredientTransferDetailFlow(transfer) {
  try {
    const { data: advancedInventoryIngredientDetail } = yield call(
      apiAdvIngTrf.getDetailIngredientTransfer,
      transfer.id
    );

    const partiallyShippedIngredients =
      advancedInventoryIngredientDetail.activities.filter(
        (activity) => activity.status === PARTIALLY_SHIPPED_THE_TRANSFER_REQUEST
      );
    const transferStatusIsPartial = !isEmpty(partiallyShippedIngredients);

    const { ingredients } = advancedInventoryIngredientDetail;

    yield put(
      actInvTrf.setTransferWithActivities(
        activityTransformer(
          advancedInventoryIngredientDetail.activities,
          ingredients,
          NAMESPACE_INGREDIENTS
        )
      )
    );

    yield put(
      actInvAdvTrf.setDetailIngredientTransfer(
        advancedInventoryIngredientDetail
      )
    );

    const transferIngredientWithCogs = yield call(
      getAllIngredientCogs,
      advancedInventoryIngredientDetail.ingredients,
      advancedInventoryIngredientDetail.from_outlet_id
    );

    if (transferStatusIsPartial) {
      const finalShippedIngredients =
        advancedInventoryIngredientDetail.activities.filter(
          (activity) => activity.status === FULLY_SHIPPED_THE_TRANSFER_REQUEST
        );

      if (isEmpty(finalShippedIngredients)) {
        yield put(
          actInvAdvTrf.setPartialIngredientsTransfer(
            partiallyShippedIngredients
          )
        );
      } else {
        const finalPartiallyShippedIngredients = [
          ...partiallyShippedIngredients,
          ...finalShippedIngredients,
        ];

        yield put(
          actInvAdvTrf.setPartialIngredientsTransfer(
            finalPartiallyShippedIngredients
          )
        );
      }
    }

    yield put(
      actInvTrf.setTransferDetail({
        ...transfer,
        items: transferIngredientWithCogs,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);

    /**
     * This error handling focus on populating required data
     * for all transferType FormDetail rendering.
     * This error occurs because one or one of the items in the transfer is/are deleted
     * in the middle of advanced transfer process.
     */
    const relatedToInventoryError =
      error.message === INGREDIENT_DOES_NOT_FOUND_MESSAGE;

    if (relatedToInventoryError) {
      const tabData = yield select(getTabData);
      const transfersType = yield select(getTransfersType);

      const deletedItemTransferData = tabData[transfersType].data.find(
        (data) => data.id === transfer.id
      )?.items;
      const deletedIngredientTransferData = tabData[transfersType].data.find(
        (data) => data.id === transfer.id
      )?.ingredients;

      yield put(
        actInvTrf.setTransferDetail({
          ...transfer,
          items: deletedItemTransferData,
          ingredients: deletedIngredientTransferData,
        })
      );
    }

    if (!relatedToInventoryError) {
      yield put(actNotif.error(error.message));
    }
  }
}

function* rerenderAllTabList(namespace: string) {
  const resGetFilteredDate = yield call(apiGetFilteredDate.getFilteredDate);
  const startDate = moment(resGetFilteredDate.data.start_date);
  const endDate = moment(resGetFilteredDate.data.end_date);

  const outletIds = getSelectedOutletIdFromLocalStorage();
  const outletId = outletIds.length > 1 ? null : outletIds[0];

  yield put(
    actInvAdvTrf.fetchAllTabTransferList(
      namespace,
      {
        start: startDate.startOf('day').format(),
        end: endDate.endOf('day').format(),
      },
      outletId
    )
  );
}

export function* onSubmitApprovalForm(request) {
  const {
    payload: { namespace, id, params },
  } = request;
  try {
    yield put(showLoadingSpinner(true));
    const isItem = namespace === NAMESPACE_ITEMS;
    const apiApproval = isItem
      ? apiAdvTrf.submitItemTransferApproval
      : apiAdvTrf.submitIngredientTransferApproval;

    const { data: resApproval } = yield call(apiApproval, id, params);

    yield call(rerenderAllTabList, namespace);

    const tabData = yield select(getTabData);
    const isAdvancedTransfer = yield select(getAdvancedTransferSetting);

    if (!tabData.APPROVAL.redDot && !isAdvancedTransfer) {
      yield put(actInvTrf.setTransferType('ALL'));
    }

    const user = yield select(getUser);
    clevertapAdvancedTransfer.advancedTransferApprove(user.email);

    yield put(actNotif.success(resApproval.data.message));

    yield put(actInvTrf.closeRightColumn());
    yield put(actInvTrf.setTransferDetail(null));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    yield call(rerenderAllTabList, namespace);
    yield put(actInvTrf.closeRightColumn());
    yield put(actInvTrf.setTransferDetail(null));
  } finally {
    yield put(showLoadingSpinner(false));
  }
}

export function* getAdvancedInventorySettings() {
  const { data } = yield call(apiInvSettings.getAdvancedInventorySettings);
  yield put(actAdvancedInventorySettings.setAdvancedInventorySettings(data));

  return data;
}

export function* setSelectedOutlets(outlets, selectedOutlets) {
  const result: any[] = [];
  if (selectedOutlets.length > 1) {
    yield put(actInvTrf.setSelectedOutlets(outlets));
  } else {
    outlets.forEach((outlet) => {
      if (outlet.id === selectedOutlets[0]) {
        result.push(outlet);
      }
    });
    yield put(actInvTrf.setSelectedOutlets(result));
  }
}

export function* pageRequest() {
  yield put(actLoadingSpinner.showPageLoadingSpinner(true));
  yield put(actInvTrf.resetState());

  yield put(actInvTrf.setExportButtonStatus(false));

  yield put(act.showProgressBar());

  yield call(getAdvancedInventorySettings);

  try {
    const respOutlets = yield call(apiOutlets.outlets);
    yield put(actInvTrf.setOutlets(respOutlets.data));

    const resGetFilteredDate = yield call(apiGetFilteredDate.getFilteredDate);
    const startDate = moment(resGetFilteredDate.data.start_date);
    const endDate = moment(resGetFilteredDate.data.end_date);

    const resCurrentInventoryDropdown = yield call(
      apiCurrentInventory.getCurrentInventoryDropdown
    );
    const currentInventoryDropdown =
      resCurrentInventoryDropdown.data.current_inventory_dropdown;

    setCurrentInventoryDropdownFromLocalStorage(currentInventoryDropdown);

    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield put(
      initialize('search', {
        outletIds,
        date: { startDate, endDate },
        tabular_view_options: 'all-transfers',
        namespace: currentInventoryDropdown,
        keyword: '',
      })
    );

    yield call(setSelectedOutlets, respOutlets.data, outletIds);

    const isAdvancedTransfer = yield select(getAdvancedTransferSetting);
    if (isAdvancedTransfer) {
      replaceAdvancedTransferBrowserHash();
    } else {
      replaceSimpleTransferBrowserHash();
    }

    yield put(actInvTrf.setTransferType('ALL'));

    const outletId = outletIds.length > 1 ? null : outletIds[0];

    const { data: allOutlets } = yield call(getAllOutlets);
    yield put(actInvTrf.setAllOutlets(allOutlets));

    // Generate advanced transfer Tab Permissions
    yield call(getAdvancedTransferTabPermissions);

    yield put(
      actInvAdvTrf.fetchAllTabTransferList(
        currentInventoryDropdown,
        {
          start: startDate.startOf('day').format(),
          end: endDate.endOf('day').format(),
        },
        outletId
      )
    );

    yield put(reset('transferItem'));

    clevertapInventoryTransfer.inventoryTransferStart({
      outletIds,
      startDate,
      endDate,
      namespace: currentInventoryDropdown,
    });
    yield put(subscriptionStatus());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setExportButtonStatus(true));
  }
}

export function* simpleTransferRequest(params, previousTransfers, namespace) {
  try {
    let respTransfers: any = null;
    if (namespace === NAMESPACE_ITEMS) {
      respTransfers = yield call(apiTrf.getTransfers, params);
    } else if (namespace === NAMESPACE_INGREDIENTS) {
      respTransfers = yield call(apiIngTrf.getTransfers, params);
    }

    const transfers = respTransfers.data.results;
    // copy array
    const newTransfers = previousTransfers.map((a) => a);

    transfers.forEach((trf) => {
      const lastTransfer = newTransfers[newTransfers.length - 1];
      if (
        newTransfers.length === 0 ||
        !moment(trf.created_at).isSame(moment(lastTransfer.created_at), 'days')
      ) {
        newTransfers.push({
          created_at: trf.created_at,
          isdaterow: true,
        });
      }
      newTransfers.push({
        ...trf,
        isdaterow: false,
      });
    });

    yield put(actInvTrf.setTransfers(newTransfers));
    yield put(actInvTrf.setCurrentPage(params.page));
    yield put(actInvTrf.setTotalPage(respTransfers.data.total_pages));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(actLoadingSpinner.showPageLoadingSpinner(false));
  }
}

export function* transfersRequest(request) {
  const {
    payload: { params, previousTransfers, namespace },
  } = request;

  yield put(act.showProgressBar());

  yield call(simpleTransferRequest, params, previousTransfers, namespace);

  yield put(act.hideProgressBar());
}

export function* onClickAdvanceRow(transfer, namespace) {
  const { partialItemTransfer, partialIngredientTransfer } = yield select(
    (state: any) => state.inventory.advancedTransfer
  );
  const partiaDataIsPresent =
    !isEmpty(partialItemTransfer) || !isEmpty(partialIngredientTransfer);

  yield put(actInvAdvTrf.errorGetItem(false));

  /**
   * If there's any partial data related to Print shipment letter
   * that was open in previous onClickRow event
   * Reset the state to empty array. So that the next onClickRow event
   * does not have other transfer detail partial data
   */
  if (partiaDataIsPresent) {
    yield put(actInvAdvTrf.setPartialItemsTransfer([]));
    yield put(actInvAdvTrf.setPartialIngredientsTransfer([]));
  }

  if (namespace === NAMESPACE_ITEMS) {
    yield call(itemTransferDetailFlow, transfer);
  }

  if (namespace === NAMESPACE_INGREDIENTS) {
    yield call(ingredientTransferDetailFlow, transfer);
  }

  yield put(initialize('shipmentForm', { isEdit: false, stopShipment: false }));
  yield put(initialize('fulfillmentForm', { isEdit: false }));

  yield put(actInvAdvTrf.setAdvancedTransferFormType('VIEW'));
  yield put(actInvTrf.openRightColumn());
}

export function* onClickRow(request) {
  const {
    payload: { transfer, namespace },
  } = request;

  yield call(onClickAdvanceRow, transfer, namespace);
}

export function* onChangeOutletIdsFlow(request) {
  const {
    payload: { outletIds, startDate, endDate, namespace },
  } = request;

  yield put(actLoadingSpinner.showPageLoadingSpinner(true));
  // reset...
  yield put(actInvTrf.resetState());

  yield put(actInvTrf.setExportButtonStatus(false));

  yield put(act.showProgressBar());

  const finalOutletIds = outletIds;

  try {
    const respOutlets = yield call(apiOutlets.outlets);
    yield put(actInvTrf.setOutlets(respOutlets.data));
    setSelectedOutletIdToLocalStorage(outletIds);
    yield call(setSelectedOutlets, respOutlets.data, outletIds);
    yield put(
      initialize('search', {
        outletIds,
        date: { startDate, endDate },
        namespace,
        keyword: '',
      })
    );
    const outletId = finalOutletIds.length > 1 ? null : finalOutletIds[0];
    yield put(
      actInvAdvTrf.fetchAllTabTransferList(
        namespace,
        {
          start: startDate.startOf('day').format(),
          end: endDate.endOf('day').format(),
        },
        outletId
      )
    );
    yield put(actInvTrf.setTransferType('ALL'));

    yield put(reset('transferItem'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setExportButtonStatus(true));
  }
}

export function* onChangeDate(request) {
  const {
    payload: { startDate, endDate, namespace, tabType, keyword },
  } = request;
  yield put(actLoadingSpinner.showPageLoadingSpinner(true));
  // reset...
  yield put(actInvTrf.resetState());

  yield put(actInvTrf.setExportButtonStatus(false));

  yield put(act.showProgressBar());
  try {
    const respOutlets = yield call(apiOutlets.outlets);
    yield put(actInvTrf.setOutlets(respOutlets.data));

    yield call(
      setSelectedOutlets,
      respOutlets.data,
      getSelectedOutletIdFromLocalStorage()
    );

    yield call(apiUpdateFilteredDate.updateFilteredDate, {
      from: moment(startDate).format('D/M/YYYY'),
      to: moment(endDate).format('D/M/YYYY'),
    });

    yield put(change('search', 'date', { startDate, endDate }));

    const outletIds = yield select(
      (state: any) => state.form.search.values.outletIds
    );
    const outletId = outletIds.length > 1 ? null : outletIds[0];

    yield put(
      actInvAdvTrf.fetchTabTransferList(
        tabType,
        namespace,
        {
          start: startDate.startOf('day').format(),
          end: endDate.endOf('day').format(),
          keyword,
        },
        outletId
      )
    );

    yield put(reset('transferItem'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setExportButtonStatus(true));
  }
}

export function* onClickCreateTransfer(request) {
  const {
    payload: { outletIds, allOutletsIds, isAdvancedTransfer },
  } = request;
  const { data: outlets } = yield call(getAllOutlets, FILTER_DELETED_OUTLETS);
  yield put(actInvTrf.setValidOutlets(outlets));

  if (isAdvancedTransfer) {
    yield put(
      initialize('transferItem', {
        fromOutlet: allOutletsIds.toString(),
        toOutlet: outletIds.toString(),
        transfers: [],
      })
    );
  } else {
    yield put(
      initialize('transferItem', {
        fromOutlet: outletIds.toString(),
        toOutlet: outletIds.toString(),
        transfers: [],
      })
    );
  }

  yield put(actInvAdvTrf.setAdvancedTransferFormType('CREATE'));
  yield put(actInvTrf.setTransferDetail(null));
  yield put(actInvTrf.openRightColumn());
}

export function* getItems(request) {
  yield put(actInvTrf.setItemsOptionIsLoading(true));

  const {
    payload: {
      outletId,
      keyword,
      page,
      namespace,
      isInit,
      isGetFromInit,
      previousAddItems,
      previousTotalPage,
      initData,
    },
  } = request;
  const perPage = 100;

  const itemOptions: any[] = [];
  // let items = []
  let respItems: any = null;
  let searchItemsdata: any[] = [];
  let currentPage;
  let totalPage = previousTotalPage;

  try {
    if (isGetFromInit) {
      if (initData) {
        const data = { data: initData };
        respItems = data;
      }
    } else {
      if (keyword) {
        yield call(delay, 300);
      }

      const apiParams: any = {
        keyword,
        page,
      };
      const apiHeaders: any = {};

      /**
       * for advanced inventory
       * we need to pass "outletId" as query params
       * and remove "outlet_id" from headers
       * following different API spec to get all items [BRAN-1011]
       */
      apiParams.outletId = outletId;
      apiHeaders.CUSTOM_OUTLET_ID = null;

      if (namespace === NAMESPACE_ITEMS) {
        respItems = yield call(
          apiItem.getItemsV1,
          {
            ...apiParams,
            category_id: null,
            size: perPage,
            is_mobile: false,
            tracked: true,
            searchCategoryName: false,
          },
          apiHeaders
        );

        if (respItems.data.items) {
          searchItemsdata = respItems.data.items;
          currentPage = page;
          totalPage = respItems.data.total_pages - 1;
        }
      } else if (namespace === NAMESPACE_INGREDIENTS) {
        respItems = yield call(
          apiIngredients.getAll,
          {
            ...apiParams,
            no_categories: true,
            per_page: perPage,
          },
          apiHeaders
        );

        if (respItems.data.results) {
          searchItemsdata = respItems.data.results;
          currentPage = page;
          totalPage = respItems.data.total_pages;
        }
      }
    }

    searchItemsdata = previousAddItems.concat(searchItemsdata);

    searchItemsdata.forEach((item) => {
      itemOptions.push({
        value: item.id,
        label: item.name,
        image: item.image.url,
        type: item.type,
      });
    });

    if (isInit) {
      yield put(
        actInvTrf.setSearchItemsInit({
          items: searchItemsdata,
          itemOptions,
          currentPage,
          totalPage,
        })
      );
    }

    yield put(
      actInvTrf.setAddItems({
        items: searchItemsdata,
        itemOptions,
        currentPage,
        totalPage,
      })
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(actInvTrf.setItemsOptionIsLoading(false));
  }
}

export function* formOnClickAddItems() {
  yield put(initialize('addTransferItems', { items: [] }));
}

export function* onModalSelectChange(request) {
  const {
    payload: { selectedItemId, items, transfers, namespace },
  } = request;

  if (selectedItemId === undefined) {
    yield put(
      initialize('addTransferItems', {
        items: [],
      })
    );
    return;
  }

  let isItemFound = false;

  yield put(initialize('addTransferItems', { items: [] }));
  yield put(actInvTrf.setIsItemAlreadyAdded(false));

  if (transfers.length) {
    transfers.forEach((trf) => {
      if (namespace === NAMESPACE_ITEMS && trf.itemId === selectedItemId) {
        isItemFound = true;
      }
      if (
        namespace === NAMESPACE_INGREDIENTS &&
        trf.ingredientId === selectedItemId
      ) {
        isItemFound = true;
      }
    });
  }

  if (!transfers.length || !isItemFound) {
    const temporaryItem = find(items, { id: selectedItemId });

    const fields: any[] = [];
    if (namespace === NAMESPACE_ITEMS) {
      temporaryItem.item_variants.forEach((iv) => {
        if (!iv.is_deleted) {
          fields.push({
            isItem: temporaryItem.item_variants.length === 1,
            itemId: temporaryItem.id,
            itemName: temporaryItem.name,
            itemIsRecipe: temporaryItem.is_recipe,
            itemVariantId: iv.id,
            itemVariantName: iv.name || temporaryItem.name,
            inStock: iv.in_stock,
            quantity: 0,
            sku: iv.sku,
            price: iv.price,
          });
        }
      });
    } else if (namespace === NAMESPACE_INGREDIENTS) {
      fields.push({
        ingredientId: temporaryItem.id,
        ingredientName: temporaryItem.name,
        unitId: temporaryItem.unit_id,
        unitName: temporaryItem.unit_name,
        inStock: temporaryItem.in_stock,
        quantity: 0,
        type: temporaryItem.type,
      });
    }

    // yield put(initialize('addTransferItems', {
    //   items: fields
    // }))
    for (let i = 0; i < fields.length; i++) {
      yield put(arrayPush('addTransferItems', 'items', fields[i]));
    }
  } else {
    yield put(actInvTrf.setIsItemAlreadyAdded(true));
  }
}

export function* onModalAddItem(request) {
  const {
    payload: { items },
  } = request;

  for (let i = 0; i < items.length; i++) {
    yield put(arrayPush('transferItem', 'transfers', items[i]));
  }

  yield put(actInvTrf.closeAddItemModal());
}

export function* resetTransfersForm() {
  yield put(arrayRemoveAll('transferItem', 'transfers'));
}

export function* onSubmitCreateTransferForm(request) {
  const {
    payload: { values },
  } = request;
  let hasError = false;

  if (values.fromOutlet === null || values.fromOutlet === '') {
    yield put(actNotif.error('Please choose from outlet'));
    hasError = true;
  }
  if (values.toOutlet === null || values.toOutlet === '') {
    yield put(actNotif.error('Please choose to outlet'));
    hasError = true;
  }
  if (values.transfers.length === 0) {
    yield put(actNotif.error('Please add Transfer Stock before saving'));
    hasError = true;
  }
  if (values.fromOutlet === values.toOutlet) {
    yield put(
      actNotif.error('Destination outlet must be different from origin outlet')
    );
    hasError = true;
  }

  if (values.transfers.length) {
    let total = 0;
    values.transfers.forEach((trf) => {
      total += parseFloat(trf.quantity);
    });
    if (total <= 0) {
      yield put(actNotif.error('Total transfer must be greater than 0'));
      hasError = true;
    }
  }

  if (!hasError) {
    yield put(actInvTrf.setShowConfirmSubmitModal(true));
  }
}

export function* onConfirmCreateTransfer(request) {
  const {
    payload: { values, date, namespace },
  } = request;

  yield put(actInvTrf.setConfirmCreateButtonStatus(true));

  let total = 0;
  let transferData: any = null;

  if (namespace === NAMESPACE_ITEMS) {
    transferData = {
      from_outlet_id: parseInt(values.fromOutlet, 10),
      outlet_id: parseInt(values.fromOutlet, 10),
      to_outlet_id: parseInt(values.toOutlet, 10),
      total: '',
      history_details: [],
      history_details_attributes: [],
      note: values.notes ? values.notes : '',
    };

    values.transfers.forEach((trf) => {
      if (trf.quantity > 0) {
        const details = {
          beginning_stock: trf.inStock,
          end_stock: trf.inStock - parseInt(trf.quantity, 10),
          item_id: trf.itemId,
          item_variant_id: trf.itemVariantId,
          item_variant_name: trf.itemVariantName
            ? trf.itemVariantName
            : trf.itemName,
          item_name: trf.itemName,
          quantity: trf.quantity,
        };

        transferData.history_details.push(details);
        transferData.history_details_attributes.push(details);

        total += parseInt(trf.quantity, 10);
      }
    });

    transferData.total = total;
  } else if (namespace === NAMESPACE_INGREDIENTS) {
    transferData = {
      from_outlet_id: parseInt(values.fromOutlet, 10),
      outlet_id: parseInt(values.fromOutlet, 10),
      to_outlet_id: parseInt(values.toOutlet, 10),
      history_details: [],
      history_details_attributes: [],
      note: values.notes ? values.notes : '',
      outlet_receiver_id: parseInt(values.toOutlet, 10),
      outlet_sender_id: parseInt(values.fromOutlet, 10),
    };

    values.transfers.forEach((trf) => {
      if (trf.quantity > 0) {
        const details = {
          beginning_stock: trf.inStock,
          end_stock: trf.inStock - parseFloat(trf.quantity),
          ingredient_id: trf.ingredientId,
          ingredient_name: trf.ingredientName,
          quantity: trf.quantity,
          unit_id: trf.unitId,
          unit_name: trf.unitName,
        };

        transferData.history_details.push(details);
        transferData.history_details_attributes.push(details);
      }
    });
  }

  try {
    yield put(act.showProgressBar());

    let resTransfer = {};
    if (namespace === NAMESPACE_ITEMS) {
      resTransfer = yield call(apiTrf.postTransfers, transferData);
    } else if (namespace === NAMESPACE_INGREDIENTS) {
      resTransfer = yield call(apiIngTrf.postTransfers, transferData);
    }

    yield put(reset('transferItem'));
    yield put(actNotif.success((resTransfer as any).data.success_msg));
    if (!moment().isBetween(date.startDate, date.endDate, 'day', '[]')) {
      yield call(apiUpdateFilteredDate.updateFilteredDate, {
        from: moment().format('D/M/YYYY'),
        to: moment().format('D/M/YYYY'),
      });
    }

    const selectedOutlet = yield select(getSelectedOutlets);
    const allOutlets = yield select(getAllOutlet);
    const toOutlet = allOutlets.find(
      (el) => el.id === transferData.to_outlet_id
    );
    clevertapInventoryTransfer.inventoryTransferCreate({
      outletFromName: selectedOutlet[0].name,
      outletToName: toOutlet.name,
      namespace,
      items: transferData.history_details,
    });

    yield call(pageRequest);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setShowConfirmSubmitModal(false));
    yield put(actInvTrf.setConfirmCreateButtonStatus(false));
  }
}

export function* onConfirmAdvancedCreateTransfer(request) {
  const {
    payload: { values, date, namespace },
  } = request;

  /**
   * These actions dispatched to prevent double click
   * so merchant won't be able to accidentally create double transfer
   */
  yield put(actInvTrf.setConfirmCreateButtonStatus(true));
  yield put(act.showProgressBar());
  yield put(act.showLoadingSpinner(true));

  const requestId = uuidv4();
  let requestBody: ItemsTransferRequestBody | IngredientsTransferRequestBody;
  const requestHeadersConfigs = {
    headers: {
      'x-request-uuid': requestId,
    },
  };

  /**
   * If user choose item with variants and just add quantity to 1 of 3 variants
   * only send to the API variant with quantity
   */
  const itemTransfersWithQuantity = values.transfers.filter(
    (transfer) => transfer.quantity > 0
  );

  if (namespace === NAMESPACE_ITEMS) {
    requestBody = {
      request_id: requestId,
      from_outlet_id: Number(values.fromOutlet),
      to_outlet_id: Number(values.toOutlet),
      note: values.notes ?? '',
      is_advance_transfer: values.isAdvancedTransfer,
      items: itemTransfersWithQuantity.map((val) => ({
        item_id: val.itemId,
        item_name: val.itemName,
        item_variant_id: val.itemVariantId,
        item_variant_name: val.itemVariantName,
        quantity: Number(val.quantity),
      })),
    };
  } else if (namespace === NAMESPACE_INGREDIENTS) {
    requestBody = {
      request_id: requestId,
      from_outlet_id: Number(values.fromOutlet),
      to_outlet_id: Number(values.toOutlet),
      note: values.notes ?? '',
      is_advance_transfer: values.isAdvancedTransfer,
      ingredients: values.transfers.map((val) => ({
        ingredient_id: val.ingredientId,
        ingredient_name: val.ingredientName,
        unit_id: val.unitId,
        unit_name: val.unitName,
        quantity: Number(val.quantity),
      })),
    };
  }

  try {
    if (itemTransfersWithQuantity.length > 50) {
      throw new Error(ERROR_MESSAGE_ITEMS_OR_INGREDIENTS_MORE_THAN_50);
    }

    if (namespace === NAMESPACE_ITEMS) {
      yield call(
        polling,
        apiAdvItemTrf.postCreateItemsTransfer,
        requestBody!,
        requestHeadersConfigs
      );
    } else if (namespace === NAMESPACE_INGREDIENTS) {
      yield call(
        polling,
        apiAdvIngTrf.postCreateIngredientsTransfer,
        requestBody!,
        requestHeadersConfigs
      );
    }

    yield put(reset('transferItem'));

    if (!moment().isBetween(date.startDate, date.endDate, 'day', '[]')) {
      yield call(apiUpdateFilteredDate.updateFilteredDate, {
        from: moment().format('D/M/YYYY'),
        to: moment().format('D/M/YYYY'),
      });
    }

    const user = yield select(getUser);
    const selectedOutlet = yield select(getSelectedOutlets);
    const allOutlets = yield select(getAllOutlet);
    const toOutlet = allOutlets.find(
      (el) => el.id === requestBody.to_outlet_id
    );
    clevertapInventoryTransfer.inventoryTransferCreate({
      outletFromName: selectedOutlet[0].name,
      outletToName: toOutlet.name,
      namespace,
      // @ts-expect-error
      items: requestBody.items ?? requestBody.ingredients,
    });

    clevertapAdvancedTransfer.advancedTransferRequest(user.email);

    yield call(pageRequest);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setShowConfirmSubmitModal(false));
    yield put(actInvTrf.setConfirmCreateButtonStatus(false));
    yield put(act.showLoadingSpinner(false));
  }
}

export function* exportTransfer(request) {
  yield put(act.showLoadingSpinner(true));
  const {
    payload: { date, namespace },
  } = request;
  try {
    const params = {
      startDate: date.start_date,
      endDate: date.end_date,
    };
    yield put(actInvTrf.startExportTransfer());
    if (namespace === NAMESPACE_ITEMS) {
      const { data } = yield call(apiTrf.getExportTransfer, params);
      yield put(actInvTrf.setIdAsyncJob(data));
    } else if (namespace === NAMESPACE_INGREDIENTS) {
      const { data } = yield call(apiIngTrf.getExportTransfer, params);
      yield put(actInvTrf.setIdAsyncJob(data));
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  }
}

export function* onNamespaceChange(request) {
  const {
    payload: { namespace },
  } = request;
  yield put(actLoadingSpinner.showPageLoadingSpinner(true));
  // reset...
  yield put(actInvTrf.resetState());
  yield put(actInvTrf.setExportButtonStatus(false));

  yield put(act.showProgressBar());

  try {
    const respOutlets = yield call(apiOutlets.outlets);
    yield put(actInvTrf.setOutlets(respOutlets.data));

    const outletIds = getSelectedOutletIdFromLocalStorage();

    const resGetFilteredDate = yield call(apiGetFilteredDate.getFilteredDate);
    const startDate = moment(resGetFilteredDate.data.start_date);
    const endDate = moment(resGetFilteredDate.data.end_date);

    setCurrentInventoryDropdownFromLocalStorage(namespace);

    yield put(change('search', 'outletIds', outletIds));
    yield put(change('search', 'date', { startDate, endDate }));
    yield put(change('search', 'namespace', namespace));
    yield put(change('search', 'keyword', ''));

    yield call(setSelectedOutlets, respOutlets.data, outletIds);

    const outletId = outletIds.length > 1 ? null : outletIds[0];

    yield put(
      actInvAdvTrf.fetchAllTabTransferList(
        namespace,
        {
          start: startDate.startOf('day').format(),
          end: endDate.endOf('day').format(),
        },
        outletId
      )
    );

    yield put(actInvTrf.setTransferType('ALL'));

    yield put(reset('transferItem'));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
    yield put(actInvTrf.setExportButtonStatus(true));
  }
}

export default function* saga() {
  yield takeLatest(INVENTORY_TRANSFER_PAGE_REQUEST, pageRequest);
  yield takeLatest(INVENTORY_TRANSFER_TRANSFERS_REQUEST, transfersRequest);
  yield takeLatest(INVENTORY_TRANSFER_ON_CLICK_ROW, onClickRow);
  yield takeLatest(
    INVENTORY_TRANSFER_ON_CHANGE_OUTLET_IDS,
    onChangeOutletIdsFlow
  );
  yield takeLatest(INVENTORY_TRANSFER_ON_CHANGE_DATE, onChangeDate);
  yield takeLatest(
    INVENTORY_TRANSFER_ON_CLICK_CREATE_TRANSFER,
    onClickCreateTransfer
  );
  yield takeLatest(INVENTORY_TRANSFER_GET_ITEMS, getItems);
  yield takeLatest(
    INVENTORY_TRANSFER_ON_MODAL_SELECT_CHANGE,
    onModalSelectChange
  );
  yield takeLatest(INVENTORY_TRANSFER_ON_MODAL_ADD_ITEM, onModalAddItem);
  yield takeLatest(INVENTORY_TRANSFER_RESET_TRANSFERS_FORM, resetTransfersForm);
  yield takeLatest(
    INVENTORY_TRANSFER_ON_SUBMIT_CREATE_TRANSFER_FORM,
    onSubmitCreateTransferForm
  );
  yield takeLatest(
    INVENTORY_TRANSFER_ON_CONFIRM_CREATE_TRANSFER,
    onConfirmCreateTransfer
  );
  yield takeLatest(
    INVENTORY_TRANSFER_ON_CONFIRM_ADVANCED_CREATE_TRANSFER,
    onConfirmAdvancedCreateTransfer
  );
  yield takeLatest(INVENTORY_TRANSFER_EXPORT, exportTransfer);
  yield takeLatest(INVENTORY_TRANSFER_ON_CLICK_ADD_ITEM, formOnClickAddItems);
  yield takeLatest(INVENTORY_TRANSFER_ON_NAMESPACE_CHANGE, onNamespaceChange);
  yield takeLatest(
    ADVANCE_INVENTORY_TRANSFER_SUBMIT_APPROVAL_FORM,
    onSubmitApprovalForm
  );
}
